import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Phone, Ticket, Loader2, CheckCircle2, AlertTriangle, RotateCcw, Printer,
  Lock, Users, BookOpen, TrendingUp, X, ShieldAlert, LogOut, Repeat,
} from "lucide-react";
import { api } from "../lib/nailApi";
import { printKioskTicket } from "../lib/kioskPrint";
import {
  readKioskSession, clearKioskSession, kioskCheckin as sessionCheckin, refreshKioskSession,
} from "../lib/kioskSession";
import "../styles/eventCheckinKiosk.css";

const KEYPAD = [
  [{ d: "1", l: "" }, { d: "2", l: "ABC" }, { d: "3", l: "DEF" }],
  [{ d: "4", l: "GHI" }, { d: "5", l: "JKL" }, { d: "6", l: "MNO" }],
  [{ d: "7", l: "PQRS" }, { d: "8", l: "TUV" }, { d: "9", l: "WXYZ" }],
  [{ d: "C", l: "XÓA", action: "clear" }, { d: "0", l: "+" }, { d: "⌫", l: "XÓA SỐ", action: "back" }],
];

const COPY = {
  vi: {
    title: "CHECK-IN SỰ KIỆN",
    subtitle: "Nhập số điện thoại đã đăng ký để nhận vé check-in.",
    placeholder: "(555) 123-4567",
    cta: "CHECK-IN & IN VÉ",
    cta_loading: "Đang xử lý…",
    welcome_lead: "CHÀO MỪNG ĐẾN VỚI",
    welcome_brand: "NAIL VIET CLUB",
    welcome_en: "WELCOME TO NAIL VIET CLUB",
    motto: "KẾT NỐI • HỌC HỎI • PHÁT TRIỂN",
    partners: "ĐỐI TÁC ĐỒNG HÀNH",
    partners_en: "OUR PARTNERS",
    powered: "POWERED BY",
    icons: ["KẾT NỐI", "HỌC HỎI", "PHÁT TRIỂN"],
    icons_en: ["Connect", "Learn", "Grow"],
    loading_title: "Đang kiểm tra đăng ký…",
    loading_subtitle: "Checking registration…",
    success_title: "Xin chào",
    success_body: "Bạn đã check-in thành công.",
    success_ticket_label: "Số vé",
    success_again: "In Lại Vé",
    success_next: "Khách Tiếp Theo",
    already_title: "Khách đã check-in",
    already_body: "Khách này đã check-in trước đó.",
    notfound_title: "Không tìm thấy đăng ký",
    notfound_body: "Vui lòng gặp nhân viên tại bàn check-in.",
    notfound_retry: "Thử Lại",
    notfound_walkin: "Thêm Walk-In",
    staff_hold: "Nhấn giữ 5 giây để mở menu nhân viên",
    staff_hold_en: "Press & hold 5 seconds for staff menu",
    staff_modal_title: "Menu nhân viên",
    staff_pin_label: "Nhập mã PIN nhân viên",
    staff_pin_submit: "Mở",
    staff_pin_error: "Mã PIN không đúng",
    walkin_title: "Thêm khách Walk-In",
    walkin_business: "Tên tiệm",
    walkin_owner: "Tên chủ tiệm",
    walkin_phone: "Số điện thoại",
    walkin_save: "Lưu & Check-In",
    walkin_back: "Đóng",
    printer_title: "Lỗi máy in",
    printer_body: "Không thể in vé tự động. Vui lòng thử lại hoặc in qua trình duyệt.",
    printer_retry: "Thử In Lại",
    printer_browser: "In Bằng Trình Duyệt",
    error_generic: "Có lỗi xảy ra. Vui lòng thử lại.",
  },
  en: {
    title: "EVENT CHECK-IN",
    subtitle: "Enter your registered phone number to check in.",
    placeholder: "(555) 123-4567",
    cta: "CHECK IN & PRINT TICKET",
    cta_loading: "Processing…",
    welcome_lead: "WELCOME TO",
    welcome_brand: "NAIL VIET CLUB",
    welcome_en: "Connect • Learn • Grow",
    motto: "CONNECT • LEARN • GROW",
    partners: "OUR PARTNERS",
    partners_en: "OUR PARTNERS",
    powered: "POWERED BY",
    icons: ["CONNECT", "LEARN", "GROW"],
    icons_en: ["Connect", "Learn", "Grow"],
    loading_title: "Checking registration…",
    loading_subtitle: "One moment please…",
    success_title: "Welcome",
    success_body: "You're checked in.",
    success_ticket_label: "Ticket",
    success_again: "Reprint Ticket",
    success_next: "Next Guest",
    already_title: "Already checked in",
    already_body: "This guest was already checked in.",
    notfound_title: "Registration not found",
    notfound_body: "Please see staff at the check-in desk.",
    notfound_retry: "Try Again",
    notfound_walkin: "Add Walk-In",
    staff_hold: "Press & hold 5 seconds for staff menu",
    staff_hold_en: "Long press to unlock staff menu",
    staff_modal_title: "Staff Menu",
    staff_pin_label: "Enter staff PIN",
    staff_pin_submit: "Unlock",
    staff_pin_error: "Wrong PIN",
    walkin_title: "Add Walk-In",
    walkin_business: "Business name",
    walkin_owner: "Owner name",
    walkin_phone: "Phone number",
    walkin_save: "Save & Check-In",
    walkin_back: "Close",
    printer_title: "Printer issue",
    printer_body: "Could not print automatically. Retry or use browser print.",
    printer_retry: "Retry Print",
    printer_browser: "Browser Print",
    error_generic: "Something went wrong. Please try again.",
  },
};

function formatUSPhone(digits) {
  const d = digits.slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export default function EventCheckinKiosk() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const kioskSession = useMemo(() => readKioskSession(), []);
  const sessionMode = Boolean(kioskSession && kioskSession.event && kioskSession.event.slug === slug);
  const [lang, setLang] = useState(() => (localStorage.getItem("nailEvent.lang") === "en" ? "en" : "vi"));
  const t = COPY[lang];
  const [event, setEvent] = useState(sessionMode ? kioskSession.event : null);
  const [sponsors, setSponsors] = useState([]);
  const [digits, setDigits] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | already | notfound | error | printer_error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [staffOpen, setStaffOpen] = useState(false);
  const [staffPin, setStaffPin] = useState("");
  const [staffPinError, setStaffPinError] = useState("");
  const [staffUnlocked, setStaffUnlocked] = useState(false);
  const holdRef = useRef(null);
  const clock = useClock();

  useEffect(() => { localStorage.setItem("nailEvent.lang", lang); }, [lang]);

  // Load event details (public endpoint) on mount.
  // If we have a kiosk session for this slug, use it; otherwise fall back to /public.
  // Server-validate the session FIRST so an expired/revoked token doesn't keep us in the kiosk.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (sessionMode) {
        // Validate server-side. On 401 the helper clears localStorage and returns null.
        const fresh = await refreshKioskSession();
        if (cancelled) return;
        if (!fresh?.event) {
          // Session was rejected — back to the login page.
          clearKioskSession();
          navigate("/checkin", { replace: true });
          return;
        }
        setEvent(fresh.event);
      }
      try {
        const { data } = await api.get(`/nail-events/${slug}/public`);
        if (!cancelled) {
          if (!sessionMode) setEvent(data);
          setSponsors(Array.isArray(data?.sponsors) ? data.sponsors : []);
        }
      } catch (e) {
        if (!cancelled && !sessionMode) setEvent({ name: "Nail Viet Club", event_date: "", location: "" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, sessionMode, navigate]);

  const onChangeEvent = useCallback(async () => {
    clearKioskSession();
    navigate("/checkin", { replace: true });
  }, [navigate]);

  const onLogoutKiosk = useCallback(async () => {
    try {
      const { kioskLogout } = await import("../lib/kioskSession");
      await kioskLogout();
    } catch (_) { clearKioskSession(); }
    navigate("/checkin", { replace: true });
  }, [navigate]);

  // Keypad handler
  const tap = useCallback((key) => {
    if (key === "clear") { setDigits(""); setErrorMsg(""); return; }
    if (key === "back") { setDigits((d) => d.slice(0, -1)); return; }
    if (!/^[0-9]$/.test(key)) return;
    setDigits((d) => (d.length >= 10 ? d : d + key));
  }, []);

  const submit = useCallback(async () => {
    if (digits.length !== 10 || state === "loading") return;
    setErrorMsg("");
    setState("loading");
    try {
      let data;
      if (sessionMode) {
        // Use the kiosk session — event_id is read from the kiosk_token on the server.
        data = await sessionCheckin({ phone: `+1${digits}`, language: lang });
      } else {
        const r = await api.post(`/events/${slug}/kiosk/checkin`, { phone: `+1${digits}`, language: lang });
        data = r.data;
      }
      if (data.status === "checked_in" || data.status === "already_checked_in") {
        setResult(data);
        setState(data.status === "already_checked_in" ? "already" : "success");
        try { await printKioskTicket({ event: data.event, ticket: data.ticket, lang }); }
        catch (_) { setState("printer_error"); }
      } else if (data.status === "not_found") {
        setState("notfound");
      } else {
        setState("error");
        setErrorMsg(data.detail || t.error_generic);
      }
    } catch (e) {
      // 401 means session expired; bounce to /checkin
      if (e?.response?.status === 401 && sessionMode) {
        clearKioskSession();
        navigate("/checkin", { replace: true });
        return;
      }
      setState("error");
      setErrorMsg(e?.response?.data?.detail || t.error_generic);
    }
  }, [digits, lang, slug, state, t.error_generic, sessionMode, navigate]);

  // Keyboard input
  useEffect(() => {
    const onKey = (e) => {
      if (state === "loading") return;
      if (e.key >= "0" && e.key <= "9") tap(e.key);
      else if (e.key === "Backspace") tap("back");
      else if (e.key === "Escape") tap("clear");
      else if (e.key === "Enter" && digits.length === 10) submit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [digits, state, submit, tap]);

  const reset = useCallback(() => {
    setDigits(""); setResult(null); setState("idle"); setErrorMsg("");
  }, []);

  // Long-press footer lock for staff menu
  const startHold = () => {
    if (holdRef.current) clearTimeout(holdRef.current);
    holdRef.current = setTimeout(() => { setStaffOpen(true); }, 5000);
  };
  const endHold = () => { if (holdRef.current) clearTimeout(holdRef.current); holdRef.current = null; };

  const tryStaffPin = (e) => {
    e?.preventDefault();
    if (!staffPin) { setStaffPinError(t.staff_pin_error); return; }
    // Soft-validate locally; backend will revalidate on any staff-only action.
    if (staffPin.length < 4) { setStaffPinError(t.staff_pin_error); return; }
    setStaffUnlocked(true); setStaffPinError("");
    sessionStorage.setItem("nailEvent.checkinPin", staffPin);
  };

  const heroUrl = event?.hero_image_url || "";
  const ctaDisabled = digits.length !== 10 || state === "loading";

  return (
    <div className="kiosk-root" data-testid="kiosk-root">
      {/* LEFT: Event banner */}
      <aside className="kiosk-left" data-testid="kiosk-left">
        {heroUrl ? (
          <img src={heroUrl} alt="" className="kiosk-hero-img" />
        ) : (
          <div className="kiosk-hero-fallback" />
        )}
        <div className="kiosk-hero-veil" />
        <div className="kiosk-hero-curves" aria-hidden="true">
          <span className="curve curve-1" />
          <span className="curve curve-2" />
        </div>
        <div className="kiosk-left-content">
          {/* NVC monogram */}
          <div className="kiosk-monogram">
            <div className="kiosk-nvc">N<span className="kiosk-nvc-v">V</span>C</div>
            <div className="kiosk-brand">NAIL VIET CLUB</div>
            <div className="kiosk-motto">KẾT NỐI • HỌC HỎI • PHÁT TRIỂN</div>
            <div className="kiosk-powered">{t.powered} <span className="kiosk-asterpos">AsterPOS</span></div>
          </div>

          {/* Welcome */}
          <div className="kiosk-welcome">
            <div className="kiosk-welcome-lead">{t.welcome_lead}</div>
            <h1 className="kiosk-welcome-brand">NAIL VIET CLUB</h1>
            <div className="kiosk-welcome-en">{lang === "vi" ? "WELCOME TO NAIL VIET CLUB" : t.welcome_en}</div>
            <div className="kiosk-divider"><span className="kiosk-divider-dot" /></div>
          </div>

          {/* Stats row */}
          <div className="kiosk-stats">
            {[
              { Icon: Users, vi: "KẾT NỐI", en: "CONNECT", sub_vi: "Connect", sub_en: "Connect" },
              { Icon: BookOpen, vi: "HỌC HỎI", en: "LEARN", sub_vi: "Learn", sub_en: "Learn" },
              { Icon: TrendingUp, vi: "PHÁT TRIỂN", en: "GROW", sub_vi: "Grow", sub_en: "Grow" },
            ].map(({ Icon, vi, en, sub_vi, sub_en }, i) => (
              <div className="kiosk-stat" key={i}>
                <div className="kiosk-stat-ring"><Icon className="kiosk-stat-icon" /></div>
                <div className="kiosk-stat-vi">{lang === "vi" ? vi : en}</div>
                <div className="kiosk-stat-en">{lang === "vi" ? sub_vi : sub_en}</div>
              </div>
            ))}
          </div>

          {/* Event info card */}
          {event && (
            <div className="kiosk-event-card" data-testid="kiosk-event-card">
              <div className="kiosk-event-row"><span className="kiosk-event-ico">📅</span><span>{event.event_date}{event.end_date && event.end_date !== event.event_date ? ` — ${event.end_date}` : ""}</span></div>
              <div className="kiosk-event-row"><span className="kiosk-event-ico">⏱</span><span>{event.event_time || ""}{event.end_time ? ` - ${event.end_time}` : ""}</span></div>
              <div className="kiosk-event-row"><span className="kiosk-event-ico">📍</span><span>{[event.venue_name, event.location].filter(Boolean).join(" — ") || event.location}</span></div>
              {event.address && <div className="kiosk-event-row kiosk-event-addr"><span className="kiosk-event-ico"></span><span>{event.address}</span></div>}
            </div>
          )}

          {/* Sponsors strip */}
          <div className="kiosk-sponsors" data-testid="kiosk-sponsors">
            <div className="kiosk-sponsors-title">
              <span>ĐỐI TÁC ĐỒNG HÀNH</span>
              <span className="kiosk-sponsors-sub">OUR PARTNERS</span>
            </div>
            <div className="kiosk-sponsors-row">
              {sponsors.length === 0 ? (
                ["AsterPOS", "VALOR", "Luqra", "Dejavoo", "CLOVER"].map((n) => (
                  <div key={n} className="kiosk-sponsor-text">{n}</div>
                ))
              ) : (
                sponsors.slice(0, 6).map((sp, i) => (
                  sp.logo_url
                    ? <img key={i} className="kiosk-sponsor-logo" src={sp.logo_url} alt={sp.name || ""} />
                    : <div key={i} className="kiosk-sponsor-text">{sp.name || "—"}</div>
                ))
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* RIGHT: Check-in card */}
      <main className="kiosk-right" data-testid="kiosk-right">
        <div className="kiosk-right-bg" aria-hidden="true" />
        {sessionMode && (
          <div className="kiosk-session-bar" data-testid="kiosk-session-bar">
            <button type="button" data-testid="kiosk-change-event" onClick={onChangeEvent} className="kiosk-session-btn">
              <Repeat className="kiosk-session-btn-icon" />{lang === "vi" ? "Đổi sự kiện" : "Change Event"}
            </button>
            <button type="button" data-testid="kiosk-logout" onClick={onLogoutKiosk} className="kiosk-session-btn">
              <LogOut className="kiosk-session-btn-icon" />{lang === "vi" ? "Đăng xuất" : "Logout"}
            </button>
          </div>
        )}
        <div className="kiosk-card" data-testid="kiosk-card">
          {/* Language toggle */}
          <div className="kiosk-lang">
            <button
              type="button" data-testid="kiosk-lang-vi"
              className={`kiosk-lang-btn ${lang === "vi" ? "active" : ""}`}
              onClick={() => setLang("vi")}>VI</button>
            <button
              type="button" data-testid="kiosk-lang-en"
              className={`kiosk-lang-btn ${lang === "en" ? "active" : ""}`}
              onClick={() => setLang("en")}>EN</button>
          </div>

          {/* Icon */}
          <div className="kiosk-card-icon">
            <CheckCircle2 className="kiosk-card-icon-svg" />
          </div>

          {/* Title */}
          <h2 className="kiosk-card-title">{t.title}</h2>
          <div className="kiosk-card-title-en">{lang === "vi" ? "EVENT CHECK-IN" : "EVENT CHECK-IN"}</div>
          <p className="kiosk-card-sub">{t.subtitle}</p>
          {lang === "vi" && <p className="kiosk-card-sub-en">Enter your registered phone number to check in.</p>}

          {/* States */}
          {(state === "idle" || state === "error") && (
            <>
              {/* Phone display */}
              <div className="kiosk-phone-display" data-testid="kiosk-phone-display">
                <div className="kiosk-phone-prefix">
                  <Phone className="kiosk-phone-icon" />
                  <span>+1</span>
                  <span className="kiosk-phone-caret">▾</span>
                </div>
                <div className={`kiosk-phone-number ${digits ? "filled" : ""}`}>
                  {digits ? formatUSPhone(digits) : t.placeholder}
                </div>
              </div>

              {/* Keypad */}
              <div className="kiosk-keypad" data-testid="kiosk-keypad">
                {KEYPAD.flat().map((k, i) => (
                  <button
                    type="button" key={i}
                    data-testid={`kiosk-key-${k.action || k.d}`}
                    onClick={() => tap(k.action || k.d)}
                    className={`kiosk-key ${k.action === "clear" ? "kiosk-key-clear" : ""} ${k.action === "back" ? "kiosk-key-back" : ""}`}
                  >
                    <div className="kiosk-key-d">{k.d}</div>
                    {k.l && <div className="kiosk-key-l">{k.l}</div>}
                  </button>
                ))}
              </div>

              {errorMsg && <div className="kiosk-inline-error" data-testid="kiosk-inline-error">{errorMsg}</div>}

              {/* CTA */}
              <button
                type="button"
                onClick={submit}
                disabled={ctaDisabled}
                data-testid="kiosk-submit"
                className="kiosk-cta"
              >
                <Ticket className="kiosk-cta-icon" />
                <span className="kiosk-cta-vi">{t.cta}</span>
                <span className="kiosk-cta-en">{lang === "vi" ? "CHECK IN & PRINT TICKET" : t.cta}</span>
              </button>
            </>
          )}

          {state === "loading" && (
            <div className="kiosk-loading" data-testid="kiosk-loading">
              <Loader2 className="kiosk-loading-spin" />
              <div className="kiosk-loading-vi">{t.loading_title}</div>
              <div className="kiosk-loading-en">{lang === "vi" ? "Checking registration…" : t.loading_subtitle}</div>
            </div>
          )}

          {state === "success" && result && (
            <div className="kiosk-result kiosk-success" data-testid="kiosk-success">
              <div className="kiosk-result-icon"><CheckCircle2 /></div>
              <div className="kiosk-result-title">{t.success_title}, {result.ticket?.business_name}</div>
              <div className="kiosk-result-sub">{t.success_body}</div>
              <div data-testid="kiosk-ticket-pill" className="kiosk-ticket-pill">{t.success_ticket_label} · <strong>{result.ticket?.number}</strong></div>
              <div className="kiosk-result-actions">
                <button data-testid="kiosk-reprint" className="kiosk-action kiosk-action-secondary" onClick={() => printKioskTicket({ event: result.event, ticket: result.ticket, lang })}>
                  <Printer /> {t.success_again}
                </button>
                <button data-testid="kiosk-next" className="kiosk-action kiosk-action-primary" onClick={reset}>{t.success_next}</button>
              </div>
            </div>
          )}

          {state === "already" && result && (
            <div className="kiosk-result kiosk-already" data-testid="kiosk-already">
              <div className="kiosk-result-icon kiosk-result-icon-gold"><AlertTriangle /></div>
              <div className="kiosk-result-title">{t.already_title}</div>
              <div className="kiosk-result-sub">{t.already_body}</div>
              <div data-testid="kiosk-ticket-pill" className="kiosk-ticket-pill">{result.ticket?.business_name} · {result.ticket?.number}</div>
              <div className="kiosk-result-actions">
                {staffUnlocked && (
                  <button data-testid="kiosk-reprint-staff" className="kiosk-action kiosk-action-secondary" onClick={() => printKioskTicket({ event: result.event, ticket: result.ticket, lang })}>
                    <Printer /> {t.success_again}
                  </button>
                )}
                <button data-testid="kiosk-next-already" className="kiosk-action kiosk-action-primary" onClick={reset}>{t.success_next}</button>
              </div>
            </div>
          )}

          {state === "notfound" && (
            <div className="kiosk-result kiosk-notfound" data-testid="kiosk-notfound">
              <div className="kiosk-result-icon kiosk-result-icon-red"><X /></div>
              <div className="kiosk-result-title">{t.notfound_title}</div>
              <div className="kiosk-result-sub">{t.notfound_body}</div>
              <div className="kiosk-result-actions">
                <button data-testid="kiosk-retry" className="kiosk-action kiosk-action-secondary" onClick={reset}><RotateCcw />{t.notfound_retry}</button>
                {staffUnlocked && (
                  <button data-testid="kiosk-walkin" className="kiosk-action kiosk-action-primary" onClick={() => alert(lang === "vi" ? "Mở luồng walk-in (TODO)" : "Walk-in flow (TODO)")}>{t.notfound_walkin}</button>
                )}
              </div>
            </div>
          )}

          {state === "printer_error" && result && (
            <div className="kiosk-result kiosk-printer-error" data-testid="kiosk-printer-error">
              <div className="kiosk-result-icon kiosk-result-icon-gold"><Printer /></div>
              <div className="kiosk-result-title">{t.printer_title}</div>
              <div className="kiosk-result-sub">{t.printer_body}</div>
              <div className="kiosk-result-actions">
                <button data-testid="kiosk-printer-retry" className="kiosk-action kiosk-action-secondary" onClick={() => printKioskTicket({ event: result.event, ticket: result.ticket, lang })}>{t.printer_retry}</button>
                <button data-testid="kiosk-printer-browser" className="kiosk-action kiosk-action-primary" onClick={() => printKioskTicket({ event: result.event, ticket: result.ticket, lang, forceBrowser: true })}>{t.printer_browser}</button>
                <button data-testid="kiosk-printer-next" className="kiosk-action kiosk-action-tertiary" onClick={reset}>{t.success_next}</button>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="kiosk-footer">
          <button
            type="button"
            data-testid="kiosk-staff-hold"
            onMouseDown={startHold} onMouseUp={endHold} onMouseLeave={endHold}
            onTouchStart={startHold} onTouchEnd={endHold}
            className="kiosk-footer-staff"
          >
            <Lock className="kiosk-footer-lock" />
            <span className="kiosk-footer-staff-vi">{t.staff_hold}</span>
            <span className="kiosk-footer-staff-en">{lang === "vi" ? "Press & hold 5 seconds for staff menu" : t.staff_hold_en}</span>
          </button>
          <div className="kiosk-footer-time" data-testid="kiosk-footer-time">
            <div className="kiosk-footer-clock">{clock.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
            <div className="kiosk-footer-date">{clock.toLocaleDateString("en-GB").replace(/\//g, "/")}</div>
          </div>
        </div>

        {/* Staff Modal */}
        {staffOpen && (
          <div className="kiosk-modal-overlay" onClick={() => setStaffOpen(false)} data-testid="kiosk-staff-modal">
            <div className="kiosk-modal" onClick={(e) => e.stopPropagation()}>
              <div className="kiosk-modal-icon"><ShieldAlert /></div>
              <h3 className="kiosk-modal-title">{t.staff_modal_title}</h3>
              <form onSubmit={tryStaffPin} className="kiosk-modal-body">
                <label className="kiosk-modal-label">{t.staff_pin_label}</label>
                <input
                  type="password" inputMode="numeric" autoComplete="off"
                  data-testid="kiosk-staff-pin-input"
                  value={staffPin}
                  onChange={(e) => setStaffPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  className="kiosk-modal-input"
                  placeholder="••••"
                />
                {staffPinError && <div className="kiosk-modal-error">{staffPinError}</div>}
                <div className="kiosk-modal-actions">
                  <button type="button" className="kiosk-action kiosk-action-tertiary" onClick={() => setStaffOpen(false)}>{t.walkin_back}</button>
                  <button type="submit" className="kiosk-action kiosk-action-primary" data-testid="kiosk-staff-pin-submit">{t.staff_pin_submit}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
